import { useCallback, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Select, Spin, Tag, TreeSelect, message } from "antd";
import {
  BankOutlined as OrganizationIcon,
  EnvironmentOutlined as SiteIcon,
  RedoOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { DataGrid, SelectColumn } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import "../spinachHarvestEntry/HarvestEntryGrid.css";
import "./BroccoliHarvestEntry.css";

import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";
import { Footer, Sidebar } from "../../../components/index.js";
import { useUndoRedo } from "./useUndoRedo.js";
import { useBroccoliHarvestEntryColumns } from "./useBroccoliHarvestEntryColumns.jsx";
import { useBroccoliHarvestEntryData } from "./useBroccoliHarvestEntryData.js";
import { STATUS, STATUS_FILTER_ALL, STATUS_FILTER_UNSAVED, STATUS_LABEL } from "./harvestEntryConstants.js";
import { fetchSiteConfig, upsertActualYields, updateConfigRecordYields } from "./harvestEntry.storage.js";
import { getSiteTimezone, siteDayjs } from "../broccoliHarvestOptimiserDashboard/siteTimezone.js";
import { v4 as uuidv4 } from 'uuid';

export default function BroccoliHarvestEntry({
  currentOrg,
  setCurrentOrg,
  orgData,
  currentSite,
  setCurrentSite,
  siteData,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteConfig, setSiteConfig] = useState(null);
  const siteTimezone = useMemo(() => getSiteTimezone(siteConfig, currentSite), [siteConfig, currentSite]);
  const [defaultHarvestDateISO, setDefaultHarvestDateISO] = useState(() =>
    siteDayjs(null, siteTimezone).format(BACKEND_DATE_FORMAT),
  );
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [paddockBlockFilter, setPaddockBlockFilter] = useState([]);
  const [saving, setSaving] = useState(false);

  const { rows, dispatch, canUndo, canRedo } = useUndoRedo();

  const currentOrgName = orgData?.find((o) => o.id === currentOrg)?.name;

  const { mergedRows, loading: dataLoading, error: dataError } = useBroccoliHarvestEntryData({
    siteName: currentSite?.name,
    organization: currentOrgName,
  });

  useEffect(() => {
    if (!currentSite?.name) return;

    const controller = new AbortController();
    const loadSiteConfig = async () => {
      const nextSiteConfig = await fetchSiteConfig(currentSite.name);
      if (!controller.signal.aborted) setSiteConfig(nextSiteConfig);
    };

    loadSiteConfig();
    return () => controller.abort();
  }, [currentSite?.name]);

  useEffect(() => {
    setDefaultHarvestDateISO(siteDayjs(null, siteTimezone).format(BACKEND_DATE_FORMAT));
  }, [siteTimezone]);

  // Initialise grid rows and clear selection + filters when site data loads
  useEffect(() => {
    if (!mergedRows) return;
    dispatch({ type: "INITIALIZE", payload: { rows: mergedRows } });
    setSelectedRows(new Set());
    setStatusFilter(STATUS_FILTER_ALL);
    setPaddockBlockFilter([]);
  }, [mergedRows, dispatch]);

  const ensurePendingDate = useCallback(
    (row) => {
      if (row.pendingHarvestDate || row.harvest_date) return row;
      return { ...row, pendingHarvestDate: defaultHarvestDateISO };
    },
    [defaultHarvestDateISO],
  );

  // Build Paddock → Block tree data for the TreeSelect filter
  const paddockBlockTreeData = useMemo(() => {
    const paddockMap = new Map();
    for (const row of rows) {
      if (!row.paddock || !row.block) continue;
      if (!paddockMap.has(row.paddock)) paddockMap.set(row.paddock, new Set());
      paddockMap.get(row.paddock).add(row.block);
    }
    return [...paddockMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([paddock, blocks]) => ({
        title: paddock,
        value: `paddock:${paddock}`,
        key: `paddock:${paddock}`,
        children: [...blocks].sort().map((block) => ({
          title: block,
          value: `${paddock}||${block}`,
          key: `${paddock}||${block}`,
        })),
      }));
  }, [rows]);

  const isLastPassOfBlock = useCallback(
    (row) => {
      const maxPass = rows.reduce(
        (max, r) =>
          r.paddock === row.paddock && r.block === row.block
            ? Math.max(max, r.pass)
            : max,
        0,
      );
      return row.pass === maxPass;
    },
    [rows],
  );

  const handleAddPass = useCallback(
    (row) => {
      dispatch({ type: "ADD_PASS", payload: { paddock: row.paddock, block: row.block } });
    },
    [dispatch],
  );

  const { columns, rowHasPendingChanges, getEffectiveStatus } = useBroccoliHarvestEntryColumns({
    ensurePendingDate,
    setDefaultHarvestDateISO,
    onAddPass: handleAddPass,
    isLastPassOfBlock,
    siteTimezone,
    displayUnits: currentSite?.metadata?.siteConfig?.display_units,
  });

  const columnsWithSelect = useMemo(
    () => [
      { ...SelectColumn, cellClass: "harvest-select-cell", headerCellClass: "harvest-select-cell" },
      ...columns,
    ],
    [columns],
  );

  // Apply paddock/block tree filter + status filter
  const filteredRows = useMemo(() => {
    // Expand selected tree nodes to a set of "paddock||block" keys
    let selectedBlockKeys = null;
    if (paddockBlockFilter.length > 0) {
      selectedBlockKeys = new Set();
      for (const v of paddockBlockFilter) {
        if (v.startsWith("paddock:")) {
          const paddock = v.slice("paddock:".length);
          rows.forEach((r) => {
            if (r.paddock === paddock) selectedBlockKeys.add(`${r.paddock}||${r.block}`);
          });
        } else {
          selectedBlockKeys.add(v);
        }
      }
    }

    return rows.filter((row) => {
      if (selectedBlockKeys && !selectedBlockKeys.has(`${row.paddock}||${row.block}`)) return false;
      if (statusFilter === STATUS_FILTER_UNSAVED) { if (!rowHasPendingChanges(row)) return false; }
      else if (statusFilter !== STATUS_FILTER_ALL && getEffectiveStatus(row) !== statusFilter) return false;
      return true;
    });
  }, [rows, paddockBlockFilter, statusFilter, getEffectiveStatus, rowHasPendingChanges]);

  const hasAnyPendingEdits = useMemo(
    () => rows.some(rowHasPendingChanges),
    [rows, rowHasPendingChanges],
  );

  // Open the status editor on single click instead of double click
  const onCellClick = useCallback(({ column, selectCell }) => {
    if (["pendingStatus", "pendingHarvestDate", "pendingYield"].includes(column.key)) selectCell(true);
  }, []);

  const onRowsChange = useCallback(
    (nextRows, { indexes }) => {
      const updatedRows = indexes.map((i) => nextRows[i]);
      dispatch({ type: "UPDATE_ROWS", payload: { updatedRows } });
    },
    [dispatch],
  );

  const handleBulkStatus = useCallback(
    (status) => {
      dispatch({
        type: "UPDATE_STATUS_BULK",
        payload: {
          keys: [...selectedRows],
          status,
          defaultDate: defaultHarvestDateISO,
        },
      });
      setSelectedRows(new Set());
    },
    [dispatch, selectedRows, defaultHarvestDateISO],
  );

  const handleSave = useCallback(async () => {
    const pendingRows = rows.filter(rowHasPendingChanges);
    if (pendingRows.length === 0) return;

    const getEffective = (row) => {
      let yieldTon = row.yield_kg ?? null;
      if (row.pendingYield !== null && row.pendingYield !== undefined) {
        // pendingYield is stored in tons (canonical unit); storage layer multiplies by 1000 to convert to kg
        yieldTon = row.pendingYield;
      }
      return {
        status: row.pendingStatus ?? row.status ?? STATUS.PLANTED,
        harvest_date: row.pendingHarvestDate ?? row.harvest_date ?? null,
        yield_kg: yieldTon,
      };
    };

    // date_of_harvest is NOT NULL in DB — skip rows with no harvest date
    const saveable = pendingRows.filter((r) => {
      const eff = getEffective(r);
      return eff.harvest_date != null;
    });

    if (saveable.length === 0) {
      message.warning("No rows have a harvest date set. Please set a harvest date before saving.");
      return;
    }

    if (saveable.length < pendingRows.length) {
      message.info(`${pendingRows.length - saveable.length} row(s) skipped — harvest date is required.`);
    }

    setSaving(true);
    try {
      const upsertPayload = saveable.map((r) => {
        const eff = getEffective(r);
        const row = {
          organization: currentOrgName,
          site: currentSite?.name,
          paddock: r.paddock,
          block: r.block,
          pass_number: r.pass,
          status: eff.status,
          date_of_harvest: eff.harvest_date,
          actual_yield: eff.yield_kg,
          harvested_head_count:
            eff.yield_kg != null && r.average_weight
              ? Math.floor((eff.yield_kg * 1000) / r.average_weight)
              : null,
        };
        if (r.id !== null) {
          row.id = r.id;
        } else {
          // id is required by upsert, hence generating id
          row.id = uuidv4()
        }
        return row;
      });

      const upserted = await upsertActualYields(upsertPayload);

      // NEW: Sync harvested_stand_count to Config Record
      try {
        // Get unique blocks from saveable rows to identify which blocks to update
        const uniqueBlocksSet = new Set();
        for (const row of saveable) {
          uniqueBlocksSet.add(`${row.paddock}||${row.block}`);
        }

        // For each block, sum ALL yields (all passes) from current state and prepare Config Record updates
        const configUpdates = [];
        for (const blockKey of uniqueBlocksSet) {
          const [paddock, block] = blockKey.split("||");

          // Sum ALL yields for this block from current state (all passes)
          const yieldSum = rows
            .filter((r) => r.paddock === paddock && r.block === block)
            .reduce((acc, r) => {
              const eff = getEffective(r);
              return acc + (eff.yield_kg ?? 0);
            }, 0);

          // Get average_weight from any row with this block
          const blockRow = rows.find((r) => r.paddock === paddock && r.block === block);
          const average_weight = blockRow?.average_weight;

          // Calculate harvested_stand_count = floor(sum / average_weight)
          const harvested_stand_count =
            yieldSum > 0 && average_weight
              ? Math.floor(yieldSum / average_weight)
              : null;

          configUpdates.push({
            site: currentSite?.name,
            paddock,
            block,
            harvested_stand_count,
          });
        }

        // Update Config Records (silent failures)
        await updateConfigRecordYields(configUpdates);
      } catch (configErr) {
        console.error("Config Record sync failed (yield save still succeeded):", configErr);
      }

      const savedRows = upserted.map((u) => ({
        _key: `${u.paddock}||${u.block}||${u.pass_number}`,
        id: u.id,
        status: u.status,
        harvest_date: u.date_of_harvest,
        yield_kg: u.actual_yield != null ? u.actual_yield / 1000 : null,
      }));

      dispatch({ type: "SAVE_SUCCESS", payload: { savedRows } });
      message.success(
        `Saved ${saveable.length} row${saveable.length > 1 ? "s" : ""}`,
      );
    } catch (err) {
      console.error("Save failed:", err);
      message.error(`Save failed: ${err?.message ?? "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  }, [rows, rowHasPendingChanges, currentSite, currentOrgName, dispatch]);

  const breadcrumbItems = [
    {
      title: (
        <Tag color="gold">
          <OrganizationIcon /> {currentOrgName}
        </Tag>
      ),
      menu: {
        items: orgData?.map((org) => ({ key: org.id, label: org.name })),
        onClick: (e) => setCurrentOrg(e.key),
      },
    },
    {
      title: (
        <Tag color="blue">
          <SiteIcon /> {currentSite?.displayName}
        </Tag>
      ),
      menu: {
        items: siteData?.map((site) => ({
          key: site.id,
          label: site.displayName,
        })),
        onClick: (e) => {
          const siteObj = siteData.find((s) => s.id === e.key);
          setCurrentSite(siteObj);
        },
      },
    },
  ];

  return (
    <div id="broccoli-harvest-entry">
      <Sidebar
        active="broccoli_harvest_entry"
        sidebarType="BroccoliGrower"
        open={sidebarOpen}
        setOpen={setSidebarOpen}
      />

      <div
        className={`flex w-full flex-col pb-14 pt-8 lg:pt-6 sm:pt-4 ${
          sidebarOpen ? "pl-[210px] lg:pl-[50px]" : "pl-[50px]"
        } transition-[padding] duration-200 ease-in-out`}
      >
        <div className="mx-auto w-full max-w-[1900px] px-8 xl:px-6 lg:px-3 md:px-2 sm:px-1.5">
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div
            className="bg-white rounded-md shadow-sm overflow-hidden"
            style={{ border: "1px solid #e5e7eb" }}
          >
            {/* Header strip */}
            <div
              className="harvest-header-strip"
              style={{
                background: "#f5f5f5",
                borderBottom: "1px solid #e5e7eb",
                padding: "6px 10px",
              }}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                {/* Left — filters + bulk actions */}
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-1.5 text-sm text-gray-600 font-medium">
                    Paddock / Block:
                    <TreeSelect
                      value={paddockBlockFilter}
                      onChange={setPaddockBlockFilter}
                      treeData={paddockBlockTreeData}
                      treeCheckable
                      allowClear
                      showCheckedStrategy={TreeSelect.SHOW_PARENT}
                      placeholder="All"
                      style={{ minWidth: 180 }}
                      popupMatchSelectWidth={false}
                      getPopupContainer={() => document.body}
                      maxTagCount="responsive"
                      listHeight={400}
                      popupRender={(menu) => (
                        <div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderBottom: "1px solid #eee" }}>
                            <span style={{ fontWeight: 700, fontSize: 13 }}>Paddock / Block Filter</span>
                            <Button size="small" onClick={() => setPaddockBlockFilter([])}>Clear</Button>
                          </div>
                          {menu}
                        </div>
                      )}
                    />
                  </label>

                  <label className="flex items-center gap-1.5 text-sm text-gray-600 font-medium">
                    Status:
                    <Select
                      value={statusFilter}
                      onChange={setStatusFilter}
                      style={{ minWidth: 140 }}
                      popupMatchSelectWidth={false}
                      options={[
                        { value: STATUS_FILTER_ALL, label: "All statuses" },
                        { value: STATUS_FILTER_UNSAVED, label: "Unsaved" },
                        ...Object.values(STATUS).map((s) => ({ value: s, label: STATUS_LABEL[s] })),
                      ]}
                    />
                  </label>

                  {/* Bulk status actions — visible only when rows are selected */}
                  {selectedRows.size > 0 && (
                    <>
                      <div style={{ width: 1, height: 20, background: "#d9d9d9", margin: "0 4px" }} />
                      <span className="text-sm text-gray-500">
                        {selectedRows.size} row{selectedRows.size > 1 ? "s" : ""} selected — Set status:
                      </span>
                      {Object.values(STATUS).map((s) => (
                        <Button key={s} size="small" onClick={() => handleBulkStatus(s)}>
                          {STATUS_LABEL[s]}
                        </Button>
                      ))}
                      <Button size="small" onClick={() => setSelectedRows(new Set())}>
                        Clear
                      </Button>
                    </>
                  )}
                </div>

                {/* Right — undo / redo / save */}
                <div className="flex items-center gap-2">
                  <Button
                    size="middle"
                    icon={<UndoOutlined />}
                    onClick={() => dispatch({ type: "UNDO" })}
                    disabled={!canUndo}
                  >
                    Undo
                  </Button>

                  <Button
                    size="middle"
                    icon={<RedoOutlined />}
                    onClick={() => dispatch({ type: "REDO" })}
                    disabled={!canRedo}
                  >
                    Redo
                  </Button>

                  <Button
                    size="middle"
                    type="primary"
                    disabled={!hasAnyPendingEdits || saving}
                    loading={saving}
                    onClick={handleSave}
                  >
                    Save all changes
                  </Button>
                </div>
              </div>
            </div>

            {/* Data Grid */}
            <div
              className="harvest-grid"
              style={{
                "--rdg-header-background-color": "#f5f5f5",
                "--rdg-selection-color": "#1677ff",
              }}
            >
              {dataLoading ? (
                <div className="flex items-center justify-center" style={{ height: 300 }}>
                  <Spin size="large" tip="Loading harvest data…" />
                </div>
              ) : dataError ? (
                <div className="flex items-center justify-center text-red-500" style={{ height: 300 }}>
                  Failed to load data. Check console for details.
                </div>
              ) : (
                <DataGrid
                  className="rdg-light harvest-rdg"
                  columns={columnsWithSelect}
                  rows={filteredRows}
                  onRowsChange={onRowsChange}
                  onCellClick={onCellClick}
                  rowKeyGetter={(r) => r.id ?? r._key}
                  selectedRows={selectedRows}
                  onSelectedRowsChange={setSelectedRows}
                  rowHeight={52}
                  headerRowHeight={72}
                  defaultColumnOptions={{ resizable: true }}
                  rowClass={(row) => {
                    const classes = [];
                    const idx = filteredRows.indexOf(row);
                    if (idx % 2 === 0) classes.push("broccoli-row--even");
                    if (row.pass === 1) classes.push("broccoli-row--block-start");
                    if (rowHasPendingChanges(row)) classes.push("harvest-row--pending");
                    return classes.join(" ") || undefined;
                  }}
                  style={{ height: "100%", width: "100%" }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
}
